import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  OnInit,
  signal,
  untracked,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { QuantityControl } from '@softapps/lager/core';
import {
  BackNavigationService,
  HeaderComponent,
  SOFTLINE_FEATURE_TITLE,
  TitleStore,
} from '@softline/application';
import { Store } from '@softline/core';
import { SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL } from '../../lagerkorrektur-artikel.shared';
import { LagerkorrekturArtikelStore } from '../../store';
import { TranslatePipe } from '@softline/ui-core';
import { type ProgressStep, ProgressStepsComponent } from '@softline/ui-core/progress';
import { PageComponent, PageHeaderComponent, PageFooterComponent } from '@softapps/core/pages';

@Component({
  selector: 'soft-mengen-auswahl',
  imports: [
    CommonModule,
    ProgressStepsComponent,
    HeaderComponent,
    QuantityControl,
    TranslatePipe,
    PageComponent,
    PageHeaderComponent,
    PageFooterComponent,
  ],
  templateUrl: './mengen-auswahl.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./mengen-auswahl.page.css'],
})
export class MengenAuswahlPage implements OnInit {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly backNavigationService = inject(BackNavigationService);

  lagerplatz$ = this.store.observe(
    SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
    LagerkorrekturArtikelStore.getters.lagerplatz
  );
  artikel$ = this.store.observe(
    SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
    LagerkorrekturArtikelStore.getters.artikel
  );

  readonly steps: ProgressStep[] = [
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.LAGERPLATZ_SCANNEN.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.LAGERPLATZ_SCANNEN.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.ARTIKEL_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.ARTIKEL_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.MENGEN_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.MENGEN_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.UMBUCHUNGSARTIKEL_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.UMBUCHUNGSARTIKEL_AUSWAHL.DESCRIPTION',
    },
  ];

  private readonly productQuantity = toSignal(this.artikel$);
  private readonly quantityChanged = signal(false);
  private readonly quantityControl = viewChild(QuantityControl);

  readonly canContinue = computed(() => {
    const artikelMenge = this.productQuantity();
    if (!artikelMenge) return false;
    if (artikelMenge.arteheingabePflicht === true) return this.quantityChanged();
    return true;
  });

  constructor() {
    effect(async () => {
      const artikelMenge = this.productQuantity();
      const comp = this.quantityControl();
      if (!artikelMenge || !comp) return;

      if (
        artikelMenge?.arteheingabe &&
        artikelMenge?.arteheingabePflicht === true &&
        !untracked(() => this.quantityChanged())
      ) {
        await untracked(() => comp.openUnitEntryModal());
      }
    });
  }

  ngOnInit(): void {
    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#LAGERKORREKTUR_ARTIKEL.TITLE'
    );
  }

  ngOnDestroy() {
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/lagerkorrektur-artikel/artikel-auswahl']);
  }

  async onMengeChange(params: {
    quantity: number | null | undefined;
    baseQuantity?: number | null;
    unitEntryId?: number | null;
  }): Promise<void> {
    this.quantityChanged.set(true);

    const artikel = this.store.get(
      SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
      LagerkorrekturArtikelStore.getters.artikel
    );
    this.store.commit(
      SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
      LagerkorrekturArtikelStore.mutations.setArtikel,
      {
        ...artikel,
        menge: params.baseQuantity ?? 0,
        menge_lg: params.quantity ?? 0,
        idarteheingabe: params?.unitEntryId ?? undefined,
      }
    );
  }

  @HostListener('window:keydown.enter')
  async onSubmit(): Promise<void> {
    await this.router.navigate(['/lagerkorrektur-artikel', 'umbuchungsartikel-auswahl']);
  }
}
